import { useState } from "react";
import dayjs from "dayjs";
import {
  Button,
  Skeleton,
  SvgIcon,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, Stack, SxProps, Theme } from "@mui/system";
import { IDarkTheme } from "styles/darkTheme";
import CheckIcon from "@mui/icons-material/Check";

import StarsIcon from "assets/stars.svg";
import { ReactComponent as SlackIcon } from "assets/slackIcon.svg";
import { PATH_NAME } from "routes/pathNames";
import { useNavigate } from "react-router-dom";
import { useCreateSubscription, useManageSubscription } from "./hooks";
import { useCurrentUser } from "app/appHooks";
import UpgradeModal from "./upgradeModal";
import PlanStar from "assets/planStar.svg";
import PlanCreditCard from "assets/planCreditCard.svg";
import PlanBankNote from "assets/planBankNote.svg";
import PlankCalendar from "assets/planCalendar.svg";
import { UserRoles } from "utils/constants";

export type IPlanCardProps =
  | {
      priceId: string;
      isLoading?: never;
      themeType: "light" | "dark";
      planName: string;
      planPeriod: "Month" | "Year" | "week" | "day" | "Trial";
      planPrice: number;
      isRecommended?: boolean;
      isCurrentPlan?: boolean;
      features?: string[];
      icon?: any;
      sx?: SxProps<Theme>;
      isLandingPage?: boolean;
      isSubscribed: boolean;
      savedValue: number;
      savedPercentage: number;
    }
  | {
      isLoading?: boolean;
      priceId?: never;
      themeType: "light" | "dark";
      planName?: never;
      planPeriod?: never;
      planPrice?: never;
      isRecommended?: boolean;
      isCurrentPlan?: boolean;
      features?: string[];
      icon?: any;
      sx?: SxProps<Theme>;
      isLandingPage?: boolean;
      isSubscribed?: boolean;
      savedValue?: number;
      savedPercentage?: number;
    };

export type IPlanItemProps = {
  label: string;
  value: string;
  icon: string;
};
const planItemsArray: IPlanItemProps[] = [
  {
    label: "Plan",
    icon: PlanStar,
    value: ""
  },
  {
    label: "Plan Price",
    icon: PlanBankNote,
    value: ""
  },
  {
    label: "Payment Method",
    icon: PlankCalendar,
    value: ""
  },
  {
    label: "Next Payment Date",
    icon: PlanCreditCard,
    value: ""
  }
];

// TODO Refactor this file into multiple components

const PlanCard = ({
  isLoading,
  priceId,
  isLandingPage,
  themeType,
  planName,
  planPeriod,
  planPrice,
  isCurrentPlan,
  isRecommended,
  features,
  icon,
  sx,
  isSubscribed,
  savedValue,
  savedPercentage
}: IPlanCardProps) => {
  const theme = useTheme<IDarkTheme>();
  const navigate = useNavigate();
  const isProPlan = !planName?.toLowerCase().startsWith("free");
  const isLightTheme = themeType === "light";
  const { data: user } = useCurrentUser();
  const hasToken = !!localStorage.getItem("token");
  const { data: authenticatedUser } = useCurrentUser(hasToken, false);
  const isLoggedIn = hasToken && !!authenticatedUser;

  const [modalOpen, setModalOpen] = useState(false);
  const { data: createData, isLoading: isCreateLoading } =
    useCreateSubscription(priceId || "", modalOpen && !isSubscribed);
  const { data: manage, isLoading: isManageLoading } = useManageSubscription(
    !!isSubscribed
  );
  const [planItems, setPlanItems] = useState(planItemsArray);
  const isBtnLoading = isManageLoading || isCreateLoading;
  const onProPlanClick = () => {
    setPlanItems((prevArray) => {
      prevArray[0].value = planName || "";
      prevArray[1].value = `$${planPrice}`;
      prevArray[2].value = "Credit Card";
      if (planPeriod === "Year") {
        prevArray[3].value = dayjs()
          .year(dayjs().year() + 1)
          .format("D MMM YYYY");
      } else {
        prevArray[3].value = dayjs()
          .month(dayjs().month() + 1)
          .format("D MMM YYYY");
      }

      return [...prevArray];
    });
    setModalOpen(true);
  };

  const handleAddToSlack = () => {
    if (isLoggedIn) {
      navigate(PATH_NAME.BILLING);
    } else {
      localStorage.setItem("redirected", "payment-stripe");
      navigate(PATH_NAME.LOGIN);
    }
  };
  const handleModalSubmit = () => {
    const stripeURL = createData || manage;
    window.location.href = stripeURL;
  };
  const handleClose = () => {
    setPlanItems(planItems);
    setModalOpen(false);
  };
  const getButton = () => {
    return (
      !isSubscribed &&
      (!user || (user.id && user.role === UserRoles.COMPANY_ADMIN)) && (
        <Button
          disabled={isCurrentPlan}
          variant={isCurrentPlan ? "text" : "contained"}
          onClick={isLandingPage ? handleAddToSlack : onProPlanClick}
          sx={{
            color: isLightTheme
              ? theme.colors.semantic.semantic_white
              : theme.colors.secondary.secondary_50,
            backgroundColor:
              isCurrentPlan || !isLightTheme
                ? theme.colors.semantic.semantic_white
                : theme.colors.semantic.secondary_50,
            textTransform: "none",
            paddingBlock: "11px",
            borderRadius: "8px",
            width: "90%",
            marginTop: "auto",
            alignSelf: "center",
            "&:hover": {
              backgroundColor: isLightTheme
                ? theme.colors.secondary.secondary_50
                : theme.colors.semantic.semantic_white
            }
          }}
        >
          {isLandingPage && (
            <Stack direction={"row"} gap={"8px"}>
              <SvgIcon component={SlackIcon} />
              <Typography
                fontWeight={theme.fontWeight.bold}
                fontSize={theme.fontSize.bodyText_1}
              >
                Add to Slack
              </Typography>
            </Stack>
          )}

          {isCurrentPlan && (
            <Typography
              sx={{
                fontSize: theme.fontSize.heading_9,
                fontFamily: theme.fontFamily.montserrat
              }}
            >
              Current Plan
            </Typography>
          )}
          {!isSubscribed && !isLandingPage ? (
            <Typography
              fontWeight={theme.fontWeight.bold}
              fontSize={theme.fontSize.bodyText_1}
            >
              Subscribe
            </Typography>
          ) : null}
          {/* {!isLandingPage && !isCurrentPlan && isSubscribed && (
          <Typography
            fontWeight={theme.fontWeight.bold}
            fontSize={theme.fontSize.bodyText_1}
          >
            Upgrade Now
          </Typography>
        )} */}
        </Button>
      )
    );
  };
  return (
    <>
      <Stack
        //Main content
        gap={"23px"}
        sx={{
          position: "relative",
          minHeight: "571px",
          width: "340px",
          maxWidth: "500px",
          padding: "32px 24px",
          borderRadius: "16px",
          boxShadow: "0px 4px 31px rgba(0, 0, 0, 0.08)",
          backgroundColor: isLightTheme
            ? theme.colors.semantic.semantic_white
            : theme.colors.secondary.secondary_50,
          background: isCurrentPlan ? "#F9F9FF" : "",
          border: isCurrentPlan
            ? `1px solid ${theme.colors.secondary.secondary_50}`
            : `1px solid ${theme.colors.neutrals.neutrals_100}`,
          ...sx
        }}
      >
        {isRecommended ? (
          <Box
            sx={{
              position: "absolute",
              padding: isLandingPage ? "12px 16px" : "5px 10px 5px 10px",
              top: isLandingPage ? "0px" : "45px",
              right: "24px",
              backgroundColor: theme.colors.semantic.semantic_light_green,
              borderRadius: isLandingPage ? "0px 0px 8px 8px" : "16px"
            }}
          >
            <Typography
              fontFamily={theme.fontFamily.montserrat}
              fontSize={theme.fontSize.bodyText_1}
              fontWeight={theme.fontWeight.bold}
              color={theme.colors.semantic.semantic_white}
            >
              Recommended
            </Typography>
          </Box>
        ) : (
          <img
            style={{
              position: "absolute",
              top: "45px",
              right: "24px"
            }}
            loading="lazy"
            src={StarsIcon}
            alt="stars"
            width="37px"
            height="37px"
          />
        )}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "51px",
            height: "51px",
            borderRadius: "8px",
            backgroundColor: isLoading
              ? "unset"
              : isProPlan
              ? theme.colors.semantic.semantic_yellow
              : theme.colors.secondary.secondary_50
          }}
        >
          {isLoading ? (
            <Skeleton sx={{ width: "51px", height: "51px" }} />
          ) : (
            <img
              style={{ maxWidth: "30px" }}
              src={icon}
              alt={"icon"}
              loading="lazy"
            />
          )}
        </Box>
        <Stack
          //Plan name and pricing
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"baseline"}
        >
          <Stack direction={"column"}>
            <Typography
              fontFamily={theme.fontFamily.montserrat}
              fontSize={theme.fontSize.heading_5}
              lineHeight={theme.fontSize.heading_3_5}
              fontWeight={theme.fontWeight.big}
              whiteSpace="pre-wrap"
              color={
                isLightTheme
                  ? theme.colors.neutrals.neutrals_100
                  : theme.colors.semantic.semantic_white
              }
            >
              {isLoading ? <Skeleton width={"100px"} /> : planName}
            </Typography>
            {!isLoading && (
              <hr
                style={{
                  border: "none",
                  borderRadius: "2px",
                  height: "4px",
                  width: "40px",
                  backgroundColor: isLightTheme
                    ? theme.colors.secondary.secondary_50
                    : theme.colors.semantic.semantic_white
                }}
              />
            )}
          </Stack>

          <Stack direction={"row"} gap={1}>
            <Typography
              fontFamily={theme.fontFamily.montserrat}
              fontSize={theme.fontSize.heading_4}
              fontWeight={theme.fontWeight.big}
              color={
                isLightTheme
                  ? theme.colors.neutrals.neutrals_100
                  : theme.colors.semantic.semantic_white
              }
            >
              {isLoading ? <Skeleton width="30px" /> : "$" + planPrice}
            </Typography>
            <Typography
              color={
                isLightTheme
                  ? theme.colors.neutrals.neutrals_70
                  : theme.colors.semantic.semantic_white
              }
              sx={{
                alignSelf: "center"
              }}
            >
              {isLoading ? <Skeleton width="40px" /> : "/" + planPeriod}
            </Typography>
          </Stack>
        </Stack>
        <Stack direction={"column"} gap="24px">
          {!isLoading &&
          savedPercentage &&
          savedPercentage > 0 &&
          planPeriod === "Year" ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
                padding: "8px 16px",
                borderRadius: "8px",
                background: isLightTheme
                  ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                  : "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                boxShadow: isLightTheme
                  ? "0px 4px 12px rgba(102, 126, 234, 0.25)"
                  : "0px 4px 12px rgba(245, 87, 108, 0.25)"
              }}
            >
              <Typography
                fontFamily={theme.fontFamily.montserrat}
                fontSize={theme.fontSize.bodyText_1}
                fontWeight={theme.fontWeight.bold}
                color={theme.colors.semantic.semantic_white}
              >
                🎉 Save {savedPercentage}% · ${savedValue} off yearly
              </Typography>
            </Box>
          ) : (
            <div style={{ height: "40px" }} />
          )}

          {isLoading
            ? Array.from(Array(5).keys()).map((i) => (
                <Skeleton width={"100%"} key={i} />
              ))
            : features?.map((feat, featIndex) => (
                <Stack direction={"row"} gap={"8px"} key={feat + Math.random()}>
                  <Box
                    sx={{
                      width: "24px",
                      height: "24px",
                      borderRadius: "50%",
                      backgroundColor: isLightTheme
                        ? theme.colors.secondary.secondary_30
                        : theme.colors.semantic.semantic_purple
                    }}
                  >
                    <CheckIcon
                      sx={{
                        width: "24px",
                        heigth: "24px",
                        color: isLightTheme
                          ? theme.colors.secondary.secondary_50
                          : theme.colors.semantic.semantic_white
                      }}
                    />
                  </Box>
                  <Typography
                    fontSize={theme.fontSize.bodyText_1}
                    fontWeight={theme.fontWeight.regular}
                    color={
                      isLightTheme
                        ? theme.colors.neutrals.neutrals_100
                        : theme.colors.semantic.semantic_white
                    }
                  >
                    {feat}
                    {!isProPlan && featIndex === 0 ? (
                      <Tooltip
                        title="It's 10 kudos per month Totaling 50 kudos. Then it's only one per month."
                        arrow
                        placement="top"
                      >
                        <InfoOutlinedIcon
                          sx={{
                            fontSize: "16px",
                            verticalAlign: "middle",
                            marginLeft: "4px",
                            cursor: "pointer",
                            color: isLightTheme
                              ? theme.colors.secondary.secondary_50
                              : theme.colors.semantic.semantic_white
                          }}
                        />
                      </Tooltip>
                    ) : null}
                  </Typography>
                </Stack>
              ))}
        </Stack>
        {isLoading ? (
          <Skeleton
            sx={{
              width: "90%",
              paddingBlock: "11px",
              borderRadius: "8px",
              marginTop: "auto",
              alignSelf: "center",
              height: "84px"
            }}
          />
        ) : (
          getButton()
        )}
      </Stack>
      <UpgradeModal
        modalOpen={modalOpen}
        planItems={planItems}
        handleClose={handleClose}
        isUpgrade={false}
        onSubmit={handleModalSubmit}
        loading={isBtnLoading}
      />
    </>
  );
};

export default PlanCard;
